import React from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Check, X, Zap, Gift, Truck, MessageCircle, Sparkles, Shield, Star, Crown, Phone, TrendingUp, Users, Target, DollarSign, Clock, Percent } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import planBasicImage from '@/assets/villatech/plan-basic.jpg';
import planPlusImage from '@/assets/villatech/plan-plus.jpg';
import planEliteImage from '@/assets/villatech/plan-elite.jpg';

// Import membership logos
import logoBronce from '@/assets/membresia-logos/logo-bronce.png';
import logoPlata from '@/assets/membresia-logos/logo-plata.png';
import logoOro from '@/assets/membresia-logos/logo-oro.png';

const MembresiaLanding: React.FC = () => {
  const whatsappNumber = "50688888888";
  const whatsappMessage = encodeURIComponent("¡Hola! Quiero activar mi membresía VillaTech");

  const openWhatsApp = () => {
    window.open(`https://wa.me/${whatsappNumber}?text=${whatsappMessage}`, '_blank');
  };

  // Discount categories from the strategy document
  const discountCategories = [
    { category: "Estuches y vidrios", bronce: "10%", plata: "15%", oro: "20%", icon: "📱" },
    { category: "Cargadores y cables", bronce: "10%", plata: "15%", oro: "20%", icon: "⚡" },
    { category: "Tecnología (Alexa, Fire)", bronce: "5%", plata: "10%", oro: "10%", icon: "📺" },
    { category: "Gaming", bronce: "5%", plata: "10%", oro: "10%", icon: "🎮" },
    { category: "Audio / Streaming", bronce: "5%", plata: "10%", oro: "10%", icon: "🔊" },
    { category: "Reparaciones", bronce: "10%", plata: "15%", oro: "20%", icon: "🛠️" },
  ];

  const steps = [
    { number: "1", text: "Activás tu membresía una sola vez", detail: "Elegí tu plan: Bronce, Plata o Oro" },
    { number: "2", text: "Automáticamente aplicás el descuento en tu compra actual", detail: "10-20% OFF inmediato" },
    { number: "3", text: "Cada mes volvés a VillaTech por tus beneficios", detail: "Vidrios, estuches, limpiezas" },
    { number: "4", text: "Coordinás todo desde WhatsApp con un asesor", detail: "Plan Oro: asesor personalizado" },
  ];

  // Updated plans with launch pricing (until January 31)
  const plans = [
    {
      id: 'bronce',
      name: 'Bronce',
      subtitle: 'Protección Básica',
      regularPrice: '₡15.000',
      launchPrice: '₡12.000',
      priceLabel: '/mes',
      color: 'amber',
      colorHex: '#92400e',
      icon: Shield,
      image: planBasicImage,
      logo: logoBronce,
      emoji: '🟫',
      targetAudience: 'Compras básicas',
      // Real costs for VillaTech
      realCost: '₡2.700',
      marginPercent: '77%',
      features: [
        { text: '1 Temperado sencillo o 1 Hidrogel HD / mes', included: true },
        { text: '1 Estuche Space / mes', included: true },
        { text: '2 Limpiezas / mes', included: true },
        { text: 'Garantía Temperado 4 horas', included: true },
        { text: 'Atención preferencial', included: false },
        { text: 'Acceso administrador', included: false },
        { text: 'Envío gratis', included: false },
      ],
      commercialMessage: "Protegés tu celular todos los meses y todo te sale más barato.",
    },
    {
      id: 'plata',
      name: 'Plata',
      subtitle: 'Protección Completa',
      regularPrice: '₡30.000',
      launchPrice: '₡27.000',
      priceLabel: '/mes',
      color: 'slate',
      colorHex: '#64748b',
      icon: Star,
      image: planPlusImage,
      logo: logoPlata,
      emoji: '🥈',
      targetAudience: 'Compras frecuentes',
      realCost: '₡5.900',
      marginPercent: '78%',
      features: [
        { text: '2 Temperados completos o 2 Hidrogel Mate o Restaurativo / mes', included: true },
        { text: '2 Estuches Space MagSafe / mes', included: true },
        { text: '2 Limpiezas / mes', included: true },
        { text: 'Garantía Temperado 3 días', included: true },
        { text: 'Atención preferencial', included: true },
        { text: 'Acceso administrador', included: false },
        { text: 'Envío gratis +₡40.000', included: true },
      ],
      commercialMessage: "Pagás menos en cada compra y tenés beneficios reales todos los meses.",
    },
    {
      id: 'oro',
      name: 'Oro',
      subtitle: 'VIP Total',
      regularPrice: '₡50.000',
      launchPrice: '₡45.000',
      priceLabel: '/mes',
      color: 'yellow',
      colorHex: '#eab308',
      icon: Crown,
      image: planEliteImage,
      logo: logoOro,
      emoji: '🥇',
      targetAudience: 'Tech lovers / VIP',
      popular: true,
      limited: true,
      realCost: '₡14.000',
      marginPercent: '69%',
      features: [
        { text: '2 Temperados completo Privado o 2 Hidrogel Premium / mes', included: true },
        { text: '1 Estuche Premium o MagSafe / mes', included: true },
        { text: '3 Limpiezas / mes', included: true },
        { text: 'Garantía Temperado 5 días', included: true },
        { text: 'Atención VIP preferencial', included: true },
        { text: 'Acceso directo administrador', included: true },
        { text: 'Envío gratis +₡20.000', included: true },
        { text: 'WhatsApp personalizado', included: true },
      ],
      commercialMessage: "Tenés línea directa con VillaTech y beneficios que nadie más tiene.",
    }
  ];

  const testimonials = [
    {
      text: "Pagué ₡12.000 por la membresía Bronce, ¡y ese mismo día me ahorré ₡7.000 en mi Alexa!",
      name: "Laura R.",
      role: "Miembro Bronce",
      image: "https://randomuser.me/api/portraits/women/44.jpg"
    },
    {
      text: "Cada mes vengo por mi limpieza y estuche. Ya no gasto más en esas cosas.",
      name: "Josué T.",
      role: "Miembro Plata",
      image: "https://randomuser.me/api/portraits/men/32.jpg"
    },
    {
      text: "Con GOLD tengo 20% en gaming y hablo directo con el asesor. Ya recuperé la inversión.",
      name: "Paola M.",
      role: "Miembro Oro",
      image: "https://randomuser.me/api/portraits/women/68.jpg"
    },
  ];

  const faqs = [
    {
      question: "¿Cuánto dura la membresía?",
      answer: "La membresía tiene una duración de 12 meses desde el momento de activación."
    },
    {
      question: "¿Puedo cambiar de plan después?",
      answer: "Sí, podés subir de plan en cualquier momento pagando la diferencia."
    },
    {
      question: "¿Cómo reclamo mis beneficios mensuales?",
      answer: "Coordinás directamente por WhatsApp con tu asesor personalizado o visitando cualquier sucursal."
    },
    {
      question: "¿El descuento se aplica en la primera compra?",
      answer: "¡Sí! El descuento se aplica inmediatamente en tu compra actual al activar la membresía."
    },
    {
      question: "¿Hay disponibilidad limitada?",
      answer: "Sí. Solo hay 50 membresías disponibles por plan hasta el 31 de enero con precio de lanzamiento."
    },
  ];

  const comparisonRows = [
    { label: "Precio de lanzamiento", bronce: "₡12.000/mes", plata: "₡27.000/mes", oro: "₡45.000/mes" },
    { label: "Precio regular", bronce: "₡15.000/mes", plata: "₡30.000/mes", oro: "₡50.000/mes" },
    { label: "Vidrios / hidrogel", bronce: "1 temperado sencillo o hidrogel HD", plata: "2 temperados completos o Hidrogel Mate/Restaurativo", oro: "2 Temperados Privado o Hidrogel Premium" },
    { label: "Estuches", bronce: "1 Space", plata: "2 Space MagSafe", oro: "1 Premium o MagSafe" },
    { label: "Limpiezas", bronce: "2 por mes", plata: "2 por mes", oro: "3 por mes" },
    { label: "Garantía de temperado", bronce: "4 horas", plata: "3 días", oro: "5 días" },
    { label: "Atención preferencial", bronce: false, plata: "Preferencial", oro: "VIP exclusiva" },
    { label: "Acceso WhatsApp personalizado", bronce: false, plata: false, oro: "✔️ Directo" },
    { label: "Envío Gratis", bronce: false, plata: "+₡40.000", oro: "+₡20.000" },
    { label: "Desc. estuches y temperados", bronce: "10%", plata: "15%", oro: "20%" },
    { label: "Desc. cables, cargadores", bronce: "10%", plata: "15%", oro: "20%" },
    { label: "Desc. tecnología general", bronce: "5%", plata: "10%", oro: "15%" },
    { label: "Desc. gaming", bronce: "5%", plata: "10%", oro: "20%" },
    { label: "Desc. reparaciones", bronce: "10%", plata: "20%", oro: "30%" },
  ];

  // Product examples for upselling
  const productExamples = [
    { name: "Alexa Echo Dot 5ta Gen", regular: "₡35.000", gold: "₡31.500", discount: "10%" },
    { name: "Audífonos Gaming Xtrike", regular: "₡26.000", gold: "₡20.800", discount: "20%" },
    { name: "Fire TV Stick 4K", regular: "₡39.000", gold: "₡35.100", discount: "10%" },
    { name: "Power Bank 10.000 mAh", regular: "₡25.000", gold: "₡22.500", discount: "10%" },
    { name: "Estuche OtterBox", regular: "₡16.000", gold: "₡14.400", discount: "10%" },
    { name: "Aro de Luz Profesional", regular: "₡25.000", gold: "₡22.500", discount: "10%" },
  ];

  // Financial projections
  const projections = [
    { metric: "Membresías activas (3 meses)", value: "300", icon: Users },
    { metric: "Ingreso mensual proyectado", value: "₡7,500,000", icon: DollarSign },
    { metric: "Utilidad estimada mensual", value: "₡5,000,000", icon: TrendingUp },
    { metric: "Aumento ticket promedio", value: "+25%", icon: Target },
  ];

  return (
    <div className="min-h-screen bg-black text-white overflow-x-hidden">
      {/* HERO SECTION */}
      <section className="relative py-20 md:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/30 via-black to-yellow-900/20" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-yellow-500/10 rounded-full blur-[150px]" />
        
        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <Badge className="mb-6 bg-yellow-500/20 text-yellow-400 border border-yellow-500/40 shadow-[0_0_20px_rgba(250,204,21,0.3)]">
              <Zap className="w-4 h-4 mr-2" />
              🔥 Oferta de lanzamiento – hasta el 31 de enero
            </Badge>

            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight">
              <span className="text-white">Activa tu membresía y tenés</span>
              <br />
              <span className="bg-gradient-to-r from-yellow-400 to-amber-500 bg-clip-text text-transparent">
                DESCUENTO INMEDIATO
              </span>
              <br />
              <span className="text-white">en este momento</span>
            </h1>

            <p className="text-xl md:text-2xl text-zinc-300 mb-4 max-w-3xl mx-auto">
              ¿Vas a pagar un producto? <span className="text-yellow-400 font-bold">¡Pará!</span> Con la membresía tenés descuento <span className="text-yellow-400 font-bold">AHORA MISMO</span> y además beneficios todos los meses.
            </p>

            <div className="flex flex-wrap items-center justify-center gap-4 mb-8">
              <div className="bg-zinc-900/80 border border-yellow-500/30 rounded-2xl px-6 py-3">
                <span className="text-2xl md:text-3xl font-bold text-yellow-400">💥 Desde ₡12.000/mes</span>
              </div>
              <Badge className="bg-red-500/20 text-red-400 border-red-500/40 px-4 py-2 animate-pulse">
                ⏰ Solo 50 por plan
              </Badge>
            </div>

            <Button
              onClick={openWhatsApp}
              size="lg"
              className="bg-gradient-to-r from-yellow-400 to-amber-500 hover:from-yellow-500 hover:to-amber-600 text-black font-bold text-xl px-10 py-7 rounded-2xl shadow-[0_0_40px_rgba(250,204,21,0.4)] hover:shadow-[0_0_60px_rgba(250,204,21,0.6)] transition-all duration-300"
            >
              👉 QUIERO MI DESCUENTO AHORA
            </Button>
          </motion.div>
        </div>
      </section>

      {/* STRATEGIC OBJECTIVE SECTION */}
      <section className="py-16 bg-gradient-to-b from-black to-zinc-900 border-y border-zinc-800">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto text-center"
          >
            <h2 className="text-2xl md:text-4xl font-bold mb-6">
              🎯 <span className="text-blue-400">Objetivo de la Estrategia</span>
            </h2>
            <p className="text-lg text-zinc-300 mb-8">
              Transformar la operación de VillaTech en un modelo <span className="text-yellow-400 font-semibold">recurrente, escalable y rentable</span> mediante membresías mensuales.
            </p>
            
            <div className="grid md:grid-cols-3 gap-4">
              {[
                { icon: TrendingUp, text: "Flujo mensual constante", color: "text-green-400" },
                { icon: Users, text: "Mayor frecuencia de compra", color: "text-blue-400" },
                { icon: Target, text: "Reducción del CAC -20%", color: "text-purple-400" },
              ].map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-zinc-900 border border-zinc-700 rounded-xl p-5
                    shadow-[inset_0_2px_4px_rgba(255,255,255,0.05),inset_0_-2px_4px_rgba(0,0,0,0.4)]"
                >
                  <item.icon className={`w-8 h-8 mx-auto mb-3 ${item.color}`} />
                  <p className="text-white font-medium">{item.text}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* PRICING TABLE - LAUNCH PROMO */}
      <section className="py-20 bg-gradient-to-b from-zinc-900 to-black">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-5xl font-bold mb-4">
              💼 <span className="text-yellow-400">Tipos de Membresía</span>
            </h2>
            <p className="text-zinc-400 text-lg">Precios promocionales hasta el 31 de enero</p>
          </motion.div>

          {/* Launch Pricing Table */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto bg-zinc-900 border border-zinc-700 rounded-3xl overflow-hidden mb-12
              shadow-[inset_0_2px_4px_rgba(255,255,255,0.05),0_8px_32px_rgba(0,0,0,0.6)]"
          >
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-zinc-800">
                    <th className="text-left p-4 text-zinc-400">Plan</th>
                    <th className="text-center p-4 text-zinc-400">Precio Regular</th>
                    <th className="text-center p-4 text-yellow-400 font-bold">Precio Lanzamiento</th>
                    <th className="text-center p-4 text-zinc-400">Disponibilidad</th>
                    <th className="text-center p-4 text-zinc-400">Dirigido a...</th>
                  </tr>
                </thead>
                <tbody>
                  {plans.map((plan, index) => (
                    <tr key={index} className="border-t border-zinc-700 hover:bg-zinc-800/50">
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <span className="text-2xl">{plan.emoji}</span>
                          <span className="text-white font-semibold">{plan.name}</span>
                        </div>
                      </td>
                      <td className="p-4 text-center text-zinc-400 line-through">{plan.regularPrice}/mes</td>
                      <td className="p-4 text-center text-yellow-400 font-bold text-lg">{plan.launchPrice}/mes</td>
                      <td className="p-4 text-center text-orange-400">50 unidades</td>
                      <td className="p-4 text-center text-zinc-300">{plan.targetAudience}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="p-4 bg-yellow-500/10 border-t border-yellow-500/30 text-center">
              <p className="text-yellow-400">📌 Esta promoción está disponible hasta el <strong>31 de enero</strong>, o hasta agotar las 50 unidades por plan.</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* MONTHLY BENEFITS SECTION */}
      <section className="py-20 bg-gradient-to-b from-black to-zinc-900">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-5xl font-bold mb-4">
              📦 <span className="text-orange-400">Beneficios por Plan</span>
            </h2>
            <p className="text-zinc-400 text-lg">Lo que recibís cada mes sin pagar nada extra</p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
            {[
              { icon: <Shield className="w-8 h-8" />, title: "Vidrios/Hidrogel premium", bronce: "1 temperado", plata: "2 temperados", oro: "2 hidrogel premium", emoji: "📦" },
              { icon: <Gift className="w-8 h-8" />, title: "Estuches nuevos", bronce: "1 Space", plata: "2 Space", oro: "1 MagSafe/Premium", emoji: "📱" },
              { icon: <Sparkles className="w-8 h-8" />, title: "Limpiezas de celular", bronce: "2/mes", plata: "2/mes", oro: "3/mes", emoji: "🧼" },
              { icon: <Clock className="w-8 h-8" />, title: "Garantía temperado", bronce: "4 horas", plata: "3 días", oro: "5 días", emoji: "🛡️" },
              { icon: <MessageCircle className="w-8 h-8" />, title: "Atención preferencial", bronce: "—", plata: "Preferencial", oro: "VIP exclusiva", emoji: "📞" },
              { icon: <Truck className="w-8 h-8" />, title: "Envío gratis desde", bronce: "—", plata: "₡40.000", oro: "₡20.000", emoji: "🚚" },
            ].map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-zinc-900 border border-zinc-700 rounded-2xl p-6 hover:border-yellow-500/50 transition-all duration-300
                  shadow-[inset_0_2px_4px_rgba(255,255,255,0.05),inset_0_-2px_4px_rgba(0,0,0,0.4),0_4px_16px_rgba(0,0,0,0.4)]"
              >
                <div className="text-4xl mb-3">{benefit.emoji}</div>
                <h3 className="text-lg font-semibold text-white mb-3">{benefit.title}</h3>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between"><span className="text-amber-600">Bronce:</span><span className="text-zinc-300">{benefit.bronce}</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">Plata:</span><span className="text-zinc-300">{benefit.plata}</span></div>
                  <div className="flex justify-between"><span className="text-yellow-400">Oro:</span><span className="text-white font-medium">{benefit.oro}</span></div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Discount Categories Table */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-zinc-900 border border-zinc-700 rounded-3xl overflow-hidden
              shadow-[inset_0_2px_4px_rgba(255,255,255,0.05),0_8px_32px_rgba(0,0,0,0.6)]"
          >
            <div className="p-6 border-b border-zinc-700">
              <h3 className="text-2xl font-bold text-center">
                💸 <span className="text-yellow-400">Descuentos por Categoría</span>
              </h3>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-zinc-700 bg-zinc-800/50">
                    <th className="text-left p-4 text-zinc-400">Categoría</th>
                    <th className="text-center p-4 text-amber-600">🟫 Bronce</th>
                    <th className="text-center p-4 text-slate-400">🥈 Plata</th>
                    <th className="text-center p-4 text-yellow-400">🥇 Oro</th>
                  </tr>
                </thead>
                <tbody>
                  {discountCategories.map((row, index) => (
                    <tr key={index} className="border-b border-zinc-800 hover:bg-zinc-800/30">
                      <td className="p-4">
                        <span className="mr-2">{row.icon}</span>
                        <span className="text-zinc-300">{row.category}</span>
                      </td>
                      <td className="p-4 text-center text-amber-500">{row.bronce}</td>
                      <td className="p-4 text-center text-slate-300">{row.plata}</td>
                      <td className="p-4 text-center text-yellow-400 font-bold">{row.oro}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>
        </div>
      </section>

      {/* SALES STRATEGY SECTION */}
      <section className="py-20 bg-gradient-to-b from-zinc-900 to-black">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-5xl font-bold mb-4">
              🛒 <span className="text-green-400">Estrategia de Venta</span>
            </h2>
            <p className="text-xl text-zinc-300 max-w-3xl mx-auto">
              <span className="text-yellow-400 font-bold">Principio clave:</span> No se vende solo "una membresía", se vende un descuento inmediato + valor mensual recurrente.
            </p>
          </motion.div>

          {/* How it activates */}
          <div className="max-w-4xl mx-auto mb-16">
            <h3 className="text-xl font-bold text-center mb-8 text-zinc-300">🧲 Cómo se activa:</h3>
            {steps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
                className="flex items-start gap-6 mb-6 last:mb-0"
              >
                <div className="flex-shrink-0 w-14 h-14 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center text-2xl font-bold text-white shadow-lg shadow-green-500/30">
                  {step.number}
                </div>
                <div className="flex-1 bg-zinc-900 border border-zinc-700 rounded-2xl p-5
                  shadow-[inset_0_2px_4px_rgba(255,255,255,0.05),inset_0_-2px_4px_rgba(0,0,0,0.4)]">
                  <p className="text-lg text-white font-medium">{step.text}</p>
                  <p className="text-sm text-zinc-400">{step.detail}</p>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Product Examples */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-zinc-900 border border-zinc-700 rounded-3xl p-8
              shadow-[inset_0_2px_4px_rgba(255,255,255,0.05),0_8px_32px_rgba(0,0,0,0.6)]"
          >
            <h3 className="text-2xl font-bold text-center mb-8">
              💰 <span className="text-yellow-400">Ejemplos Reales de Descuento</span>
            </h3>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {productExamples.map((product, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-zinc-800/50 border border-zinc-600 rounded-xl p-4 hover:border-yellow-500/50 transition-all"
                >
                  <p className="text-white font-medium mb-2">{product.name}</p>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-zinc-500 line-through">{product.regular}</span>
                    <span className="text-yellow-400 font-bold">{product.gold}</span>
                  </div>
                  <Badge className="bg-green-500/20 text-green-400 border-green-500/40">
                    {product.discount} OFF con GOLD
                  </Badge>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* PLANS SECTION - Using Membresias.tsx card design */}
      <section className="py-20 bg-gradient-to-b from-black via-blue-950/20 to-black">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-8"
          >
            <h2 className="text-3xl md:text-5xl font-bold mb-4">
              🎯 <span className="text-blue-400">Elegí tu membresía</span>
            </h2>
            <p className="text-xl text-zinc-300">Elegí tu plan y activalo ahora mismo</p>
          </motion.div>

          <div className="flex flex-wrap justify-center gap-4 mb-12">
            <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/40 px-6 py-2">
              🔥 Precio de lanzamiento hasta el 31 de enero
            </Badge>
            <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/40 px-6 py-2">
              🟡 Solo 50 membresías por plan
            </Badge>
          </div>

          {/* Plan Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {plans.map((plan, index) => {
              const Icon = plan.icon;
              const isAmber = plan.color === 'amber';
              const isSlate = plan.color === 'slate';
              const isYellow = plan.color === 'yellow';
              
              return (
                <motion.div
                  key={plan.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.3 + index * 0.1 }}
                  className="relative"
                >
                  {/* Card */}
                  <div 
                    className={`relative h-full rounded-3xl overflow-hidden transition-all duration-300
                      bg-zinc-900 border border-zinc-700
                      shadow-[inset_0_2px_4px_rgba(255,255,255,0.05),inset_0_-2px_4px_rgba(0,0,0,0.4),0_8px_32px_rgba(0,0,0,0.6)]
                      hover:shadow-[inset_0_2px_4px_rgba(255,255,255,0.08),0_0_40px_${plan.colorHex}40,0_8px_32px_rgba(0,0,0,0.6)]
                      hover:border-opacity-80
                      ${isAmber ? 'hover:border-amber-600/60' : ''}
                      ${isSlate ? 'hover:border-slate-400/60' : ''}
                      ${isYellow ? 'hover:border-yellow-400/60 ring-2 ring-yellow-400/50 shadow-[0_0_60px_rgba(250,204,21,0.2)]' : ''}
                    `}
                  >
                    {/* Popular/Limited Badge */}
                    {plan.popular && (
                      <div className="absolute top-4 right-4 z-10 bg-gradient-to-r from-yellow-400 to-amber-500 text-black text-xs font-bold px-3 py-1 rounded-full shadow-[0_4px_12px_rgba(250,204,21,0.4)]">
                        ⭐ SOLO 50 DISPONIBLES
                      </div>
                    )}
                    
                    {/* Plan Image */}
                    <div className="relative h-48 overflow-hidden">
                      <img 
                        src={plan.image} 
                        alt={plan.name}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-zinc-900" />
                      
                      {/* Logo overlay */}
                      <div className="absolute bottom-4 left-6 w-16 h-16 rounded-2xl overflow-hidden bg-white/10 backdrop-blur-sm flex items-center justify-center">
                        <img src={plan.logo} alt={`Logo ${plan.name}`} className="w-12 h-12 object-contain" />
                      </div>
                    </div>
                    
                    <div className="p-6">
                      {/* Plan Emoji & Name */}
                      <div className="flex items-center gap-3 mb-1">
                        <span className="text-3xl">{plan.emoji}</span>
                        <h3 className="text-2xl font-bold text-white">{plan.name}</h3>
                      </div>
                      <p className={`text-sm mb-4 ${isAmber ? 'text-amber-500' : ''} ${isSlate ? 'text-slate-300' : ''} ${isYellow ? 'text-yellow-400' : ''}`}>
                        {plan.subtitle}
                      </p>
                      
                      {/* Price */}
                      <div className="mb-2">
                        <div className="text-zinc-500 text-sm line-through">{plan.regularPrice}/mes</div>
                        <div className="flex items-baseline gap-2">
                          <span className={`text-4xl font-bold ${isAmber ? 'text-amber-500' : ''} ${isSlate ? 'text-slate-300' : ''} ${isYellow ? 'text-yellow-400' : ''}`}>
                            {plan.launchPrice}
                          </span>
                          <span className="text-white/50 text-sm">{plan.priceLabel}</span>
                        </div>
                      </div>

                      {/* Commercial Message */}
                      <div className={`text-sm italic mb-4 p-3 rounded-lg ${isAmber ? 'bg-amber-500/10 text-amber-400' : ''} ${isSlate ? 'bg-slate-500/10 text-slate-300' : ''} ${isYellow ? 'bg-yellow-500/10 text-yellow-400' : ''}`}>
                        "{plan.commercialMessage}"
                      </div>
                      
                      <Separator className="bg-zinc-700/50 my-5" />
                      
                      {/* Features */}
                      <ul className="space-y-3 mb-6">
                        {plan.features.map((feature, fIndex) => (
                          <li key={fIndex} className="flex items-start gap-3">
                            {feature.included ? (
                              <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5
                                ${isAmber ? 'bg-amber-600/20' : ''} ${isSlate ? 'bg-slate-400/20' : ''} ${isYellow ? 'bg-yellow-400/20' : ''}
                              `}>
                                <Check className={`w-3 h-3 ${isAmber ? 'text-amber-500' : ''} ${isSlate ? 'text-slate-300' : ''} ${isYellow ? 'text-yellow-400' : ''}`} />
                              </div>
                            ) : (
                              <div className="w-5 h-5 rounded-full bg-zinc-800 flex-shrink-0 mt-0.5 shadow-[inset_0_2px_4px_rgba(0,0,0,0.4)]">
                                <X className="w-3 h-3 text-zinc-600 m-1" />
                              </div>
                            )}
                            <span className={feature.included ? 'text-white/90 text-sm' : 'text-white/30 text-sm'}>
                              {feature.text}
                            </span>
                          </li>
                        ))}
                      </ul>

                      {/* CTA Button */}
                      <Button
                        onClick={openWhatsApp}
                        className={`w-full py-6 text-lg font-bold rounded-xl transition-all duration-300 ${
                          isYellow
                            ? 'bg-gradient-to-r from-yellow-400 to-amber-500 hover:from-yellow-500 hover:to-amber-600 text-black'
                            : 'bg-zinc-800 hover:bg-zinc-700 text-white border border-zinc-600'
                        }`}
                      >
                        {plan.emoji} Elegir {plan.name}
                      </Button>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Full Comparison Table */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-zinc-900 border border-zinc-700 rounded-3xl overflow-hidden
              shadow-[inset_0_2px_4px_rgba(255,255,255,0.05),inset_0_-2px_4px_rgba(0,0,0,0.4),0_8px_32px_rgba(0,0,0,0.6)]"
          >
            <div className="p-6 border-b border-zinc-700">
              <h3 className="text-2xl font-bold text-center">💳 Comparador Completo de Planes</h3>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-zinc-700">
                    <th className="text-left p-4 text-zinc-400">Beneficio</th>
                    <th className="text-center p-4 text-amber-600">🟫 Bronce</th>
                    <th className="text-center p-4 text-slate-400">🥈 Plata</th>
                    <th className="text-center p-4 text-yellow-400">🥇 Oro</th>
                  </tr>
                </thead>
                <tbody>
                  {comparisonRows.map((row, index) => (
                    <tr key={index} className="border-b border-zinc-800 hover:bg-zinc-800/30">
                      <td className="p-4 text-zinc-300">{row.label}</td>
                      <td className="p-4 text-center">
                        {typeof row.bronce === 'boolean' ? (
                          row.bronce ? <Check className="w-5 h-5 text-green-400 mx-auto" /> : <X className="w-5 h-5 text-zinc-600 mx-auto" />
                        ) : (
                          <span className="text-zinc-300">{row.bronce}</span>
                        )}
                      </td>
                      <td className="p-4 text-center">
                        {typeof row.plata === 'boolean' ? (
                          row.plata ? <Check className="w-5 h-5 text-green-400 mx-auto" /> : <X className="w-5 h-5 text-zinc-600 mx-auto" />
                        ) : (
                          <span className="text-zinc-300">{row.plata}</span>
                        )}
                      </td>
                      <td className="p-4 text-center">
                        {typeof row.oro === 'boolean' ? (
                          row.oro ? <Check className="w-5 h-5 text-green-400 mx-auto" /> : <X className="w-5 h-5 text-zinc-600 mx-auto" />
                        ) : (
                          <span className="text-yellow-400 font-medium">{row.oro}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>
        </div>
      </section>

      {/* VIP VALUE SECTION */}
      <section className="py-20 bg-gradient-to-b from-black to-yellow-950/20 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-yellow-500/10 via-transparent to-transparent" />
        
        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-4xl mx-auto"
          >
            <h2 className="text-3xl md:text-5xl font-bold mb-6">
              🧠 <span className="text-yellow-400">Clave del Valor VIP (ORO)</span>
            </h2>
            
            <div className="bg-zinc-900/80 border border-yellow-500/30 rounded-3xl p-8 mb-8
              shadow-[0_0_60px_rgba(250,204,21,0.15)]">
              <p className="text-3xl md:text-4xl font-bold text-yellow-400 mb-6">
                "No pasás por mostrador, hablás directo con quien manda."
              </p>
              
              <div className="grid md:grid-cols-2 gap-4 text-left">
                {[
                  "✔️ Contacto directo con el administrador de VillaTech",
                  "✔️ Atención prioritaria y personalizada",
                  "✔️ Gestión de estuches y pedidos sin fricción",
                  "✔️ Resolución rápida de garantías y reparaciones",
                  "✔️ Sensación real de cliente premium",
                ].map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="text-lg text-zinc-200"
                  >
                    {item}
                  </motion.div>
                ))}
              </div>
            </div>

            <Button
              onClick={openWhatsApp}
              size="lg"
              className="bg-gradient-to-r from-yellow-400 to-amber-500 hover:from-yellow-500 hover:to-amber-600 text-black font-bold text-xl px-10 py-7 rounded-2xl shadow-[0_0_40px_rgba(250,204,21,0.4)]"
            >
              🥇 Quiero ser VIP GOLD
            </Button>
          </motion.div>
        </div>
      </section>

      {/* FINANCIAL PROJECTIONS SECTION */}
      <section className="py-20 bg-gradient-to-b from-zinc-900 to-black">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-5xl font-bold mb-4">
              📈 <span className="text-green-400">Proyección Financiera</span>
            </h2>
            <p className="text-zinc-400 text-lg">Objetivos a 3 meses</p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {projections.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-zinc-900 border border-zinc-700 rounded-2xl p-6 text-center
                  shadow-[inset_0_2px_4px_rgba(255,255,255,0.05),0_4px_16px_rgba(0,0,0,0.4)]"
              >
                <item.icon className="w-10 h-10 mx-auto mb-4 text-green-400" />
                <p className="text-3xl font-bold text-white mb-2">{item.value}</p>
                <p className="text-sm text-zinc-400">{item.metric}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS SECTION */}
      <section className="py-20 bg-gradient-to-b from-black to-purple-950/20">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-5xl font-bold mb-4">
              🟪 <span className="text-purple-400">Testimonios reales</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
                className="bg-zinc-900 border border-zinc-700 rounded-2xl p-8
                  shadow-[inset_0_2px_4px_rgba(255,255,255,0.05),inset_0_-2px_4px_rgba(0,0,0,0.4),0_4px_16px_rgba(0,0,0,0.4)]"
              >
                <div className="text-4xl mb-4">💬</div>
                <p className="text-lg text-zinc-300 mb-6 italic">"{testimonial.text}"</p>
                <div className="flex items-center gap-4">
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                  <div>
                    <div className="font-semibold text-white">{testimonial.name}</div>
                    <div className="text-sm text-zinc-400">{testimonial.role}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ SECTION */}
      <section className="py-20 bg-zinc-900">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-5xl font-bold mb-4">
              📄 <span className="text-zinc-200">Preguntas frecuentes</span>
            </h2>
          </motion.div>

          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-zinc-800/50 border border-zinc-700 rounded-xl p-6
                  shadow-[inset_0_2px_4px_rgba(0,0,0,0.3)]"
              >
                <h3 className="text-lg font-semibold text-white mb-2">{faq.question}</h3>
                <p className="text-zinc-400">{faq.answer}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA SECTION */}
      <section className="py-24 bg-gradient-to-b from-zinc-900 via-red-950/30 to-black relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-red-500/10 via-transparent to-transparent" />

        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-3xl mx-auto"
          >
            <h2 className="text-3xl md:text-5xl font-bold mb-6">
              🎉 Aprovechá los descuentos de lanzamiento <span className="text-red-400">antes del 31 de enero</span>
            </h2>

            <p className="text-xl text-zinc-300 mb-4">
              🟢 Activá tu membresía, ahorrá <span className="text-yellow-400 font-bold">HOY</span> en tu compra y volvé cada mes por más.
            </p>

            <p className="text-lg text-zinc-400 mb-8 italic">
              "No se trata de vender vidrio. Se trata de generar frecuencia, confianza y recurrencia."
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <Button
                onClick={openWhatsApp}
                size="lg"
                className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-bold text-xl px-10 py-7 rounded-2xl shadow-[0_0_40px_rgba(34,197,94,0.4)] hover:shadow-[0_0_60px_rgba(34,197,94,0.6)] transition-all duration-300"
              >
                🛒 Activar Membresía y Canjear Descuento Ahora
              </Button>
            </div>

            <button
              onClick={openWhatsApp}
              className="inline-flex items-center gap-2 text-zinc-400 hover:text-white transition-colors"
            >
              <Phone className="w-5 h-5" />
              <span>📲 ¿Tenés dudas? Hablá con un asesor en WhatsApp</span>
            </button>

            {/* Scarcity Notice */}
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5 }}
              className="mt-12 inline-flex items-center gap-2 bg-red-500/20 border border-red-500/40 rounded-full px-6 py-3"
            >
              <span className="animate-pulse w-2 h-2 bg-red-500 rounded-full" />
              <span className="text-red-400 font-medium">⚠️ 50 membresías por plan disponibles – Válido hasta el 31 de enero</span>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-black border-t border-zinc-800">
        <div className="container mx-auto px-6 text-center text-zinc-500 text-sm">
          <p>© 2025 VillaTech Costa Rica. Todos los derechos reservados.</p>
          <p className="mt-2">🧾 Condiciones generales del beneficio aplican.</p>
        </div>
      </footer>
    </div>
  );
};

export default MembresiaLanding;
